import React, { useMemo, useState, useCallback, useEffect } from 'react';
import GooglePlacesAutocomplete, { geocodeByAddress, getLatLng } from 'react-google-places-autocomplete';
import { GoogleMap, useJsApiLoader, MarkerF, Circle } from '@react-google-maps/api';
import { getCurrentLocation, getAddressFromCoords } from '../../utils/locationHelper';
import './LocationPickerModal.css';

const LocationPickerModal = ({ isOpen, onClose, onConfirm, initialCenter }) => {
  const libraries = useMemo(() => ['places'], []);
  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries
  });

  const [mapCenter, setMapCenter] = useState(initialCenter || { lat: 22.7196, lng: 75.8577 });
  const [addressLine, setAddressLine] = useState('');
  const [isDetecting, setIsDetecting] = useState(false);

  const [inputValue, setInputValue] = useState('');

  const [houseNo, setHouseNo] = useState('');
  const [landmark, setLandmark] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  const updateLocation = useCallback(async (lat, lng, formattedAddr = null) => {
    try {
      const addressData = await getAddressFromCoords(lat, lng);
      const fullAddr = formattedAddr || addressData.formattedAddress;
      setAddressLine(fullAddr);
      setMapCenter({ lat, lng });
      
      // Auto-fill house number if Google provides it
      if (addressData.houseNumber) {
        setHouseNo(prev => prev ? prev : addressData.houseNumber);
      }
      return { lat, lng, fullAddr, addressData };
    } catch (err) {
      console.error("Error updating location:", err);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      const center = initialCenter || { lat: 22.7196, lng: 75.8577 }; // Central Indore fallback
      setMapCenter(center);
      updateLocation(center.lat, center.lng);
    }
  }, [isOpen]); // Only run when modal opens, prevent reset on typing

  const handleManualSearch = async (text) => {
    if (!text) return;
    try {
      const results = await geocodeByAddress(text);
      if (results && results[0]) {
        const coords = await getLatLng(results[0]);
        await updateLocation(coords.lat, coords.lng, results[0].formatted_address);
      }
    } catch (err) {
    }
  };

  const handleConfirm = () => {
    const finalAddress = `${houseNo ? houseNo + ', ' : ''}${landmark ? landmark + ', ' : ''}${addressLine}`;
    onConfirm({ 
      lat: mapCenter.lat, 
      lng: mapCenter.lng, 
      address: finalAddress,
      rawAddress: addressLine,
      houseNo,
      landmark
    });
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="location-modal-overlay">
      <div className="location-modal-content" style={{ position: 'relative' }}>
        {errorMessage && (
          <div style={{
            position: 'absolute', top: '16px', left: '50%', transform: 'translateX(-50%)',
            backgroundColor: '#eff6ff', border: '1.5px solid #3b82f6', color: '#1e3a8a',
            padding: '12px 20px', borderRadius: '12px', boxShadow: '0 10px 25px rgba(59,130,246,0.25)',
            display: 'flex', alignItems: 'center', gap: '10px', zIndex: 1000000, fontWeight: '600',
            fontSize: '13px', width: '90%', justifyContent: 'space-between', animation: 'fadeInDown 0.3s ease'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontSize: '18px' }}>💡</span>
              <span>{errorMessage}</span>
            </div>
            <button onClick={() => setErrorMessage('')} style={{ background: 'transparent', border: 'none', color: '#1e3a8a', cursor: 'pointer', fontWeight: 'bold', fontSize: '16px' }}>✕</button>
          </div>
        )}
        <div className="location-modal-header">
          <h3>Select Delivery Location</h3>
          <button onClick={onClose} className="close-modal">×</button>
        </div>
        
        <div className="location-modal-body">
          <div className="autocomplete-container" style={{
            transition: 'all 0.3s ease',
            borderRadius: '16px',
            boxShadow: errorMessage ? '0 0 0 4px rgba(59,130,246,0.3)' : 'none'
          }}>
            {isLoaded ? (
              <GooglePlacesAutocomplete
                autocompletionRequest={{
                  componentRestrictions: { country: [] },
                }}
                selectProps={{
                  inputValue: inputValue,
                  onInputChange: (val) => {
                    setInputValue(val);
                    if (errorMessage) setErrorMessage('');
                  },
                  onChange: async (place) => {
                    if (!place) return;
                    const results = await geocodeByAddress(place.label);
                    const coords = await getLatLng(results[0]);
                    await updateLocation(coords.lat, coords.lng, place.label);
                    setInputValue(place.label); // Keep selection in box
                    setErrorMessage('');
                  },
                  onKeyDown: (e) => {
                    if (e.key === 'Enter') {
                      handleManualSearch(inputValue);
                    }
                  },
                  placeholder: "🔍 Search area, street, pin code...",
                  isClearable: true,
                  noOptionsMessage: () => inputValue.length > 2 ? "Searching..." : "Keep typing...",
                  formatOptionLabel: (data) => (
                    <div className="zomato-option">
                      <div className="option-icon">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z" />
                          <circle cx="12" cy="10" r="3" />
                        </svg>
                      </div>
                      <div className="option-text">
                        <span className="main-text">{data.label.split(',')[0]}</span>
                        <span className="sub-text">{data.label.split(',').slice(1).join(',')}</span>
                      </div>
                    </div>
                  ),
                  styles: {
                    control: (provided) => ({
                      ...provided,
                      borderRadius: '16px',
                      padding: '8px',
                      border: errorMessage ? '1.5px solid #3b82f6' : '1.5px solid #eee',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                      backgroundColor: errorMessage ? '#f8fafc' : '#fff'
                    }),
                    menu: (provided) => ({ ...provided, zIndex: 10002, borderRadius: '16px', padding: '8px' }),
                    option: (provided, state) => ({
                      ...provided,
                      backgroundColor: state.isFocused ? '#fff5f5' : '#fff',
                      borderRadius: '8px',
                      cursor: 'pointer'
                    })
                  }
                }}
              />
            ) : (
              <div style={{ padding: '16px', textAlign: 'center', color: '#64748b', fontSize: '14px' }}>Loading area search...</div>
            )}
          </div>

          <button 
            className="modal-detect-btn-v3"
            onClick={async () => {
              setIsDetecting(true);
              try {
                const coords = await getCurrentLocation();
                await updateLocation(coords.lat, coords.lng);
              } catch (error) {
                console.error("Location detection error:", error);
                setErrorMessage("Location access is disabled. Please type your area or street name in the search box above!");
                setTimeout(() => {
                  const inputEl = document.querySelector('.autocomplete-container input');
                  if (inputEl) inputEl.focus();
                }, 100);
              } finally {
                setIsDetecting(false);
              }
            }}
            disabled={isDetecting}
          >
            <svg className={isDetecting ? "spinning" : ""} width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z" />
              <circle cx="12" cy="9" r="2" />
            </svg>
            {isDetecting ? "Detecting..." : "Use Current Location"}
          </button>

          <div className="modal-main-content">
            <div className="modal-map-container">
              {isLoaded ? (
                <GoogleMap
                  mapContainerStyle={{ width: '100%', height: '100%', borderRadius: '16px' }}
                  center={mapCenter}
                  zoom={16}
                  onClick={async (e) => await updateLocation(e.latLng.lat(), e.latLng.lng())}
                  options={{ streetViewControl: false, mapTypeControl: false, fullscreenControl: false }}
                >
                  <MarkerF position={mapCenter} draggable={true} onDragEnd={async (e) => await updateLocation(e.latLng.lat(), e.latLng.lng())} />
                  <Circle center={mapCenter} radius={50} options={{ fillColor: '#C5192D', fillOpacity: 0.1, strokeColor: '#C5192D', strokeWeight: 1, clickable: false }} />
                </GoogleMap>
              ) : (
                <div className="map-loading animate-pulse" style={{ width: '100%', height: '100%', background: '#e5e7eb', borderRadius: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9ca3af', fontWeight: 600 }}>🗺️ Loading Map...</div>
              )}
            </div>

            <div className="address-details-form">
              <div className="selected-address-preview">
                <p className="preview-label">Area / Sector</p>
                <p className="preview-value">{addressLine || "Select on map"}</p>
              </div>

              <div className="detail-inputs">
                <input 
                  type="text" 
                  placeholder="House No. / Building Name / Floor *" 
                  value={houseNo}
                  onChange={(e) => setHouseNo(e.target.value)}
                  className="modal-detail-input"
                />
                <input 
                  type="text" 
                  placeholder="Nearby Landmark (Optional)" 
                  value={landmark}
                  onChange={(e) => setLandmark(e.target.value)}
                  className="modal-detail-input"
                />
              </div>
            </div>
          </div>

          <button 
            className="confirm-location-btn"
            onClick={handleConfirm}
            disabled={!addressLine || !houseNo}
          >
            Confirm & Proceed
          </button>
        </div>
      </div>
    </div>
  );
};

export default LocationPickerModal;
